/**
 * `NativeAdCard` — sponsored card integrated into the design system.
 *
 * Non-negotiable rules embodied by the component:
 *   - **Always marked as an ad** (badge "Anuncio"), never disguised as app
 *     content.
 *   - **CLS = 0**: the slot has reserved height from the first frame, so
 *     that is already visible never moves.
 *   - **No fill ⇒ collapses to `null`**. Never a spinner or empty waiting
 *     space: no ad means nothing.
 */
import { msg } from "@lingui/core/macro";
import { createResource, Show } from "solid-js";
import { Megaphone } from "@fleet/icons";
import type { ListtoPlacementId } from "@listto/shared";
import { Badge, Card, CardContent } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { useAds } from "../ads-context";

interface NativeAdCardProps {
  placement: ListtoPlacementId;
  /** Reserved slot height (CLS=0). Must match the feed metric. */
  minHeight?: string;
}

export function NativeAdCard(props: NativeAdCardProps) {
  const { t } = useI18n();
  const ads = useAds();

  // `prepare` is best-effort: its result decides whether the slot exists.
  const [fill] = createResource(
    () => (ads?.gate(props.placement).allow ? props.placement : undefined),
    async (placement) => {
      const provider = ads?.provider();
      return provider ? provider.prepare("native", placement) : false;
    },
  );

  return (
    <Show when={ads?.live() && fill() === true}>
      <Card
        class="overflow-hidden"
        style={{ "min-height": props.minHeight ?? "7.5rem" }}
        data-testid={`native-ad-${props.placement}`}
        aria-label={t(msg`Advertisement`)}
      >
        <CardContent class="flex h-full flex-col justify-between gap-2 p-4">
          <Badge variant="secondary" class="w-fit gap-1">
            <Megaphone class="size-3" aria-hidden="true" />
            {t(msg`Ad`)}
          </Badge>
          {/* El creativo lo pinta el SDK dentro de este contenedor reservado. */}
          <div class="min-h-16 flex-1" data-ad-slot={props.placement} />
        </CardContent>
      </Card>
    </Show>
  );
}
